import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Home, Lock, Mail, User, Phone, MapPin, ArrowLeft } from 'lucide-react';
import CompanyLogo from '../components/CompanyLogo';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../i18n/LanguageContext';
import { COMPANY } from '../config/company';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const HomeownerRegister = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    password: '',
    confirmPassword: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error(language === 'es' ? 'Las contraseñas no coinciden' : 'Passwords do not match');
      return;
    }

    if (formData.password.length < 4) {
      toast.error(language === 'es' ? 'La contraseña debe tener al menos 4 caracteres' : 'Password must be at least 4 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/homeowner/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          password: formData.password
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(language === 'es' ? '¡Cuenta creada! Por favor inicie sesión.' : 'Account created! Please log in.');
        navigate('/homeowner/login');
      } else {
        toast.error(data.detail || (language === 'es' ? 'Error al registrar' : 'Registration failed'));
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(language === 'es' ? 'Error de conexión' : 'Connection error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D97706' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      {/* Language Toggle */}
      <div className="absolute top-4 right-4">
        <LanguageToggle />
      </div>

      {/* Back to Home */}
      <Link to="/" className="absolute top-4 left-4 flex items-center text-gray-600 hover:text-amber-600">
        <ArrowLeft className="h-4 w-4 mr-1" />
        {language === 'es' ? 'Inicio' : 'Home'}
      </Link>

      <Card className="w-full max-w-md shadow-2xl border-0 relative z-10">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-full" style={{ backgroundColor: `${COMPANY.primaryColor}15` }}>
              <Home className="h-10 w-10" style={{ color: COMPANY.primaryColor }} />
            </div>
          </div>
          <div className="flex justify-center mb-4">
            <CompanyLogo className="h-12 w-auto" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-800">
            {language === 'es' ? 'Crear Cuenta' : 'Create Account'}
          </CardTitle>
          <p className="text-gray-500 text-sm mt-1">
            {language === 'es' ? 'Regístrese para ver el estado de su proyecto' : 'Sign up to view your project status'}
          </p>
        </CardHeader>
        
        <CardContent className="pt-4">
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <Label className="flex items-center text-sm">
                <User className="h-3 w-3 mr-1" /> {language === 'es' ? 'Nombre Completo' : 'Full Name'} *
              </Label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={language === 'es' ? 'Su nombre' : 'Your name'}
                required
              />
            </div>

            <div>
              <Label className="flex items-center text-sm">
                <Mail className="h-3 w-3 mr-1" /> Email *
              </Label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="email@example.com"
                required
              />
            </div>

            <div>
              <Label className="flex items-center text-sm">
                <Phone className="h-3 w-3 mr-1" /> {language === 'es' ? 'Teléfono' : 'Phone'}
              </Label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <Label className="flex items-center text-sm">
                <MapPin className="h-3 w-3 mr-1" /> {language === 'es' ? 'Dirección' : 'Address'}
              </Label>
              <Input
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder={language === 'es' ? 'Su dirección' : 'Your address'}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="flex items-center text-sm">
                  <Lock className="h-3 w-3 mr-1" /> {language === 'es' ? 'Contraseña' : 'Password'} *
                </Label>
                <Input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••"
                  required
                />
              </div>
              <div>
                <Label className="text-sm">{language === 'es' ? 'Confirmar' : 'Confirm'} *</Label>
                <Input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full text-white"
              style={{ backgroundColor: COMPANY.primaryColor }}
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {language === 'es' ? 'Registrando...' : 'Creating...'}
                </span>
              ) : (
                language === 'es' ? 'Crear Cuenta' : 'Create Account'
              )}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t text-center">
            <p className="text-sm text-gray-500">
              {language === 'es' ? '¿Ya tiene cuenta?' : 'Already have an account?'}
            </p>
            <Link 
              to="/homeowner/login" 
              className="text-sm font-medium hover:underline"
              style={{ color: COMPANY.primaryColor }}
            >
              {language === 'es' ? 'Iniciar Sesión' : 'Log In'}
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="absolute bottom-4 text-center text-sm text-gray-400">
        <p>{COMPANY.name} • {COMPANY.phone}</p>
      </div>
    </div>
  );
};

export default HomeownerRegister;
